import { AutoComplete } from "@components/AutoComplete/AutoComplete";
import { KeyboardArrowDown } from "@mui/icons-material";
import { styled, Typography, useTheme } from "@mui/material";
import { onInfoShow } from "@utils/onInfoShow";
import { MouseEvent, useState } from "react";

interface FormAutocompleteProps {
  label?: string;
  dropdownPlaceholder: string;
  errorMessage?: string;
  options: { label: string; value: string }[];
  onChange: (value: string) => void;
  placeholder?: string;
  actionButtonText?: string;
  actionButtonOnClick?: () => void;
  value?: string;
  disabled?: boolean;
}
export const FormAutocomplete = ({
  errorMessage,
  options,
  onChange,
  placeholder,
  actionButtonText,
  actionButtonOnClick,
  dropdownPlaceholder,
  label,
  value,
  disabled,
}: FormAutocompleteProps) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (disabled) {
      onInfoShow(
        "This value is preset when adding form this page, go to stock or to the desired product to change it"
      );
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const selectedOption = options.find((o) => o.value === value);

  return (
    <div
      style={{
        flexDirection: "column",
        display: "flex",
        gap: "12px",
        flex: 1,
      }}
    >
      {label && (
        <Typography variant="body2" color={theme.palette.text.secondary}>
          {label}
        </Typography>
      )}
      <TextContainer
        errorMessage={errorMessage}
        onClick={handleClick}
        disabled={disabled}
        data-testid={label ? `autocomplete-${label}` : undefined}
      >
        <Typography
          variant="body2"
          color={
            selectedOption?.label
              ? theme.palette.text.primary
              : theme.palette.text.secondary
          }
        >
          {selectedOption?.label || dropdownPlaceholder}
        </Typography>
        <KeyboardArrowDown
          sx={{
            color: errorMessage
              ? theme.palette.mode === "light"
                ? "#fddcd7"
                : "#541c15"
              : theme.palette.mode === "light"
              ? "#b2b2b2"
              : "#4d4d4d",
          }}
        />
      </TextContainer>
      {errorMessage && (
        <Typography variant="subtitle1" color="error">
          {errorMessage}
        </Typography>
      )}
      <AutoComplete
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        actionButtonText={actionButtonText}
        actionButtonOnClick={actionButtonOnClick}
        anchorEl={anchorEl}
        handleClose={() => setAnchorEl(null)}
      />
    </div>
  );
};
const TextContainer = styled("div")<{
  errorMessage?: string;
  disabled?: boolean;
}>(({ theme, errorMessage }) => ({
  all: "unset",
  boxSizing: "border-box",
  padding: "8px 12px",
  height: "36px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: errorMessage
    ? theme.palette.mode === "dark"
      ? "#1d1412"
      : "#fffcfc"
    : theme.palette.mode === "dark"
    ? "#222"
    : "#17171707",
  borderRadius: "6px",
  border: `1px solid ${
    errorMessage ? theme.palette.error.dark : theme.palette.divider
  }`,
  color: theme.palette.text.primary,
  fontSize: "14px",
  "&:disabled": {
    cursor: "not-allowed",
    fontWeight: 400,
    opacity: 0.5,
    color: "inherit",
  },
  "::placeholder": {
    color: errorMessage
      ? theme.palette.error.light
      : theme.palette.mode === "light"
      ? "#b2b2b2"
      : "#4d4d4d",

    opacity: 1,
  },
  "&:hover": {
    cursor: "pointer",
  },
}));
